<script lang="ts">
    import { Avatar } from '@skeletonlabs/skeleton';
    import { FileDropzone, getToastStore, getModalStore, ProgressRadial } from "@skeletonlabs/skeleton";
    import Upload from "$lib/assets/Upload.svg";
    import { supabase } from "$lib/supabase";

    const toastStore = getToastStore();
    const modalStore = getModalStore();

    const avatar_api_endpoint = "/api/avatars"

    let user_id = $modalStore[0].meta?.user_id

    const timestamp = new Date().getTime();
    let current_avatar_url: string | undefined = supabase.storage
            .from("avatars")
            .getPublicUrl(user_id + "/" + user_id,).data.publicUrl + `?cachebuster=${timestamp}`;

    let avatar_staging = null;
    let preview_url: string | undefined = undefined;

    let uploading_avatar = false;

    let urlCheckResponse = fetch(current_avatar_url, { method: "HEAD" });

    $: {
        urlCheckResponse
            .then(resp => {
                if (!resp.ok) {
                    current_avatar_url = undefined;
                }
            })
            .catch(() => {
                current_avatar_url = undefined;
            });
        }

    async function handleFileChange(event) {
        const file = event.target.files ? event.target.files[0] : event.detail.files[0];

        if (file) {
            avatar_staging = file;

            // Create a preview URL for the image
            const reader = new FileReader();
            reader.onload = (e) => {
                preview_url = e.target.result;
            };
            const blob = await resizeImage(avatar_staging)
            reader.readAsDataURL(blob);
        }
    }

    function resetDropzone() {
        avatar_staging = null;
        preview_url = undefined;
    }

    async function resizeImage(file, width=128, height=128) {
        // Create image element to load file
        const img = new Image();
        img.src = URL.createObjectURL(file);
        
        // Load the image
        await new Promise(resolve => img.onload = resolve);
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = width;
        canvas.height = height;
        
        // Draw image 128x128 (default) on canvas
        ctx?.drawImage(img, 0, 0, width, height);
        
        // Get resized image as a blob
        const blob = await new Promise<Blob>((resolve, reject) => {
                canvas.toBlob((blob) => {
                    if (blob === null) {
                        reject(new Error('Failed to create Blob'));
                    } else {
                        resolve(blob);
                    }
                }, 'image/jpeg');
            });

        // Clean the temp url
        URL.revokeObjectURL(img.src);
        
        return blob;
    }

    async function handleUpload() {
        if (avatar_staging) {
            const blob = await resizeImage(avatar_staging);

            const formData = new FormData();
            formData.append('file', blob);

            try {
                uploading_avatar = true;
                const response = await fetch(avatar_api_endpoint, {
                    method: "PUT",
                    body: formData,
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`Something went wrong. ${errorText}`);
                }

                const result = await response.json();
                current_avatar_url = result.data;
                uploading_avatar = false;

                toastStore.trigger({
                            message: "✓ User profile picture updated!",
                            background: "bg-zinc-800 text-green-400 text-center",
                            hideDismiss: true,
                            timeout: 4000,
                        });

                const modalSettings = $modalStore[0];
                if (modalSettings && modalSettings.response) {
                    modalSettings.response({ new_url: current_avatar_url });
                }

            } catch (error) {
                toastStore.trigger({
                            message: "✗ It seems like something went wrong. Please try again or file a bug report if the error persists.",
                            background: "bg-zinc-800 text-red-400 text-center",
                            hideDismiss: true,
                            timeout: 8000,
                        });
            }

            modalStore.close();
        }
    }
</script>

{#if $modalStore[0]}
    <div class="bg-surface-500 p-5 rounded-lg flex flex-col items-center">
        <header class="font-semibold text-3xl p-5">Upload Profile Picture</header>
        {#await urlCheckResponse}
            <ProgressRadial class="m-auto" value={undefined} />
        {:then resp}
            <div class="w-full flex items-center justify-center pt-1">
                <FileDropzone
                    name="files"
                    class={`w-40 h-40 rounded-full flex items-center justify-center ${!preview_url && !current_avatar_url ? 'bg-secondary-600 bg-opacity-5 border-zinc-400' : ''}`}
                    multiple={false}
                    accept=".jpg,.png"
                    disabled={uploading_avatar}
                    on:change={handleFileChange}
                >
                    <svelte:fragment slot="lead">
                        {#if preview_url || current_avatar_url}
                            <Avatar src={preview_url || current_avatar_url} width="w-full" rounded="rounded-full" />
                        {:else}
                            <div class="w-full"><img class="mx-auto" src={Upload} /></div>
                        {/if}
                    </svelte:fragment>
                
                    <svelte:fragment slot="message">
                        <!-- This is to prevent it from falling back to its default message -->
                        {#if true} {/if} 
                    </svelte:fragment>
                
                    <svelte:fragment slot="meta">
                        {#if !preview_url && !current_avatar_url} JPG, PNG supported {/if} 
                    </svelte:fragment>
                </FileDropzone>
            </div>
        {/await}

        <div class="flex justify-between w-full mt-4">
            <div class="p-1 pl-4">
                <input
                    class="btn variant-filled-primary"
                    disabled={!preview_url || uploading_avatar}
                    value={uploading_avatar ? "Saving..." : "Save Changes"}
                    type="submit"
                    on:click={handleUpload}
                />
            </div>

            <div class="p-1 pr-4">
                <input
                    class="btn variant-filled-primary"
                    disabled={!preview_url || uploading_avatar}
                    value="Reset"
                    type="button"
                    on:click={resetDropzone}
                />
            </div>
        </div>
    </div>
{/if}